import { BracesIcon, CopyIcon, FileTextIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, SendIcon, SparklesIcon, Trash2Icon, UserRoundIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { PageIntro } from '@/components/PageIntro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { describeTrigger } from '@/features/automation/describe';
import { useAutomation } from '@/features/automation/queries';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtRelative } from '@/lib/format';
import { BUILTIN_VARIABLES, CONTACT_VARIABLES, variableLabel } from '@/lib/templates';
import type { MessageTemplateDto } from '@/lib/types';
import { cn } from '@/lib/utils';
import { TemplateEditorDialog } from './TemplateEditorDialog';
import { TokenText, VariableChip } from './TokenText';
import { useJobTemplates, useOrgTemplates, useTemplateMutations } from './queries';

/**
 * Message templates for one job (its own plus the organization's) or for the whole organization.
 * Templates feed the "Start from…" menu on Compose. Variables in double braces are either filled
 * by the app (job, clock, sender), personalized for each recipient (`{{contact.firstName}}`), or
 * asked for when the template is used. Job admins manage job templates; Super Users manage
 * organization ones.
 */
export function JobTemplatesPage() {
  const { job, permissions } = useJobContext();
  const list = useJobTemplates(job.id);
  const m = useTemplateMutations({ jobId: job.id });
  return (
    <TemplatesView
      title="Templates"
      description={`The notices ${job.name} sends again and again, ready to start from on Compose. Variables fill themselves, personalize per person, or ask you for the blanks.`}
      items={list.data?.items}
      loading={list.isPending}
      error={list.isError ? list.error : undefined}
      onRetry={() => void list.refetch()}
      canManage={permissions.manageTemplates}
      canManageScope={(t) => t.scope === 'job' && permissions.manageTemplates}
      scopeLabel={`A template for ${job.name}`}
      mutations={m}
      useHref={(t) => `/jobs/${job.id}/compose?template=${t.id}`}
      composeHref={`/jobs/${job.id}/compose`}
      compact
      orgHref="/org/templates"
      showScope
      automationJobId={job.id}
    />
  );
}

export function OrgTemplatesPage() {
  const { isOrgAdmin } = useAuth();
  const list = useOrgTemplates();
  const m = useTemplateMutations({ org: true });
  return (
    <TemplatesView
      title="Message templates"
      description="Organization-wide notices every job can start from. Jobs add their own on their Templates tab."
      items={list.data?.items}
      loading={list.isPending}
      error={list.isError ? list.error : undefined}
      onRetry={() => void list.refetch()}
      canManage={isOrgAdmin}
      canManageScope={() => isOrgAdmin}
      scopeLabel="A template for every job in the organization"
      mutations={m}
      compact
    />
  );
}

function TemplatesView({
  title,
  description,
  items,
  loading,
  error,
  onRetry,
  canManage,
  canManageScope,
  scopeLabel,
  mutations,
  useHref,
  composeHref,
  orgHref,
  showScope,
  compact,
  automationJobId,
}: {
  title: string;
  description: string;
  items: MessageTemplateDto[] | undefined;
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
  canManage: boolean;
  canManageScope: (t: MessageTemplateDto) => boolean;
  scopeLabel: string;
  mutations: ReturnType<typeof useTemplateMutations>;
  /** Where "Use" takes a template (Compose with it pre-selected); absent on the organization page. */
  useHref?: (t: MessageTemplateDto) => string;
  composeHref?: string;
  /** Where organization templates are edited, for the read-only view from a job. */
  orgHref?: string;
  showScope?: boolean;
  /** Inside a job (under the masthead): a quiet intro line instead of a page title. */
  compact?: boolean;
  /** A job's page also lists the messages its automation rules send, so every template lives in one place. */
  automationJobId?: string;
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<MessageTemplateDto | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [deleting, setDeleting] = useState<MessageTemplateDto | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState<MessageTemplateDto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const categories = useMemo(() => [...new Set((items ?? []).map((t) => t.category).filter((c): c is string => !!c))].sort(), [items]);
  const personalCount = (items ?? []).filter((t) => t.variables.some((v) => v.kind === 'contact')).length;

  const openNew = () => {
    setEditing(null);
    setReadOnly(false);
    setEditorOpen(true);
  };
  const open = (t: MessageTemplateDto) => {
    setEditing(t);
    setReadOnly(!canManageScope(t));
    setEditorOpen(true);
  };

  const duplicate = async (t: MessageTemplateDto) => {
    try {
      await mutations.create.mutateAsync({ name: `${t.name} (copy)`, body: t.body, category: t.category });
      toast.success(t.scope === 'organization' && showScope ? `Copied “${t.name}” to this job` : 'Template duplicated');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const removeMany = async (list: MessageTemplateDto[]) => {
    setBusy(true);
    let ok = 0;
    for (const t of list) {
      try {
        await mutations.remove.mutateAsync(t.id);
        ok++;
      } catch {
        /* summarised */
      }
    }
    setBusy(false);
    if (ok === list.length) toast.success(`Deleted ${ok} ${ok === 1 ? 'template' : 'templates'}`);
    else toast.warning(`Deleted ${ok} of ${list.length}; the rest failed`);
  };

  const headerActions = (
    <div className="flex flex-wrap gap-2">
      {composeHref && (
        <Button asChild variant="outline">
          <Link to={composeHref}>Open Compose</Link>
        </Button>
      )}
      {canManage && (
        <Button onClick={openNew}>
          <PlusIcon /> New template
        </Button>
      )}
    </div>
  );

  const columns: Array<Column<MessageTemplateDto>> = [
    {
      id: 'name',
      header: 'Template',
      sortValue: (t) => t.name,
      searchValue: (t) => `${t.name} ${t.category ?? ''} ${t.body} ${t.variables.map((v) => v.key).join(' ')}`,
      csv: (t) => t.name,
      className: 'w-full max-w-0',
      cell: (t) => (
        <div className="min-w-0 py-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{t.name}</span>
            {showScope && t.scope === 'organization' && (
              <Badge variant="outline" title="Shared by the organization; edit it under Organization → Templates">
                Organization
              </Badge>
            )}
            {t.variables.some((v) => v.kind === 'contact') && (
              <Badge variant="secondary" className="gap-1" title="Names, companies or titles are filled in for each recipient">
                <UserRoundIcon className="size-3" aria-hidden /> Personalized
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1 line-clamp-2 max-w-[640px] text-xs leading-relaxed">
            <TokenText body={t.body} />
          </p>
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      sortValue: (t) => t.category ?? '',
      csv: (t) => t.category ?? '',
      className: 'hidden md:table-cell',
      cell: (t) => (t.category ? <Badge variant="muted">{t.category}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    {
      id: 'fillins',
      header: 'Asks for',
      csv: (t) =>
        t.variables
          .filter((v) => v.kind === 'custom')
          .map((v) => v.key)
          .join(' '),
      sortValue: (t) => t.variables.filter((v) => v.kind === 'custom').length,
      className: 'hidden lg:table-cell',
      cell: (t) => {
        const custom = t.variables.filter((v) => v.kind === 'custom');
        if (custom.length === 0) return <span className="text-muted-foreground text-xs">Nothing, sends as written</span>;
        return (
          <div className="flex max-w-[240px] flex-wrap gap-1">
            {custom.map((v) => (
              <VariableChip key={v.key} kind="custom">
                {variableLabel(v.key)}
              </VariableChip>
            ))}
          </div>
        );
      },
    },
    {
      id: 'used',
      header: 'Used',
      align: 'right',
      sortValue: (t) => t.useCount,
      csv: (t) => t.useCount,
      className: 'whitespace-nowrap',
      cell: (t) => (
        <span className="text-muted-foreground text-xs tabular-nums">
          <span className="text-foreground text-sm">{t.useCount}</span>
          {t.lastUsedAt ? <span className="block">{fmtRelative(t.lastUsedAt)}</span> : <span className="block">never</span>}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      noExport: true,
      className: 'w-[1%] whitespace-nowrap',
      cell: (t) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {useHref && (
            <Button asChild size="sm" variant="outline">
              <Link to={useHref(t)} title="Open Compose with this template">
                <SendIcon /> Use
              </Link>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${t.name}`}>
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => open(t)}>
                {canManageScope(t) ? <PencilIcon /> : <FileTextIcon />} {canManageScope(t) ? 'Edit' : 'View'}
              </DropdownMenuItem>
              {canManage && (
                <DropdownMenuItem onClick={() => void duplicate(t)}>
                  <CopyIcon /> {t.scope === 'organization' && showScope ? 'Copy to this job' : 'Duplicate'}
                </DropdownMenuItem>
              )}
              {!canManageScope(t) && orgHref && (
                <DropdownMenuItem asChild>
                  <Link to={orgHref}>
                    <SparklesIcon /> Organization templates
                  </Link>
                </DropdownMenuItem>
              )}
              {canManageScope(t) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setDeleting(t)}>
                    <Trash2Icon /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      {compact ? (
        <PageIntro title={title} description={description} actions={headerActions} />
      ) : (
        <PageHeader title={title} description={description} actions={headerActions} />
      )}

      {items && items.length > 0 && (
        <dl className="grid grid-cols-2 gap-px border bg-border sm:grid-cols-4">
          <Stat label="Templates" value={items.length} />
          <Stat label="Categories" value={categories.length} />
          <Stat label="Personalized" value={personalCount} hint="fill in names per recipient" />
          <Stat label="Uses" value={items.reduce((n, t) => n + t.useCount, 0)} hint="from Compose" />
        </dl>
      )}

      <DataTable
        data={items}
        loading={loading}
        error={error}
        onRetry={onRetry}
        columns={columns}
        rowId={(t) => t.id}
        onRowClick={open}
        mobileCard={(t) => (
          <div className="grid gap-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-medium">{t.name}</span>
                  {t.category && <Badge variant="muted">{t.category}</Badge>}
                  {showScope && t.scope === 'organization' && <Badge variant="outline">Organization</Badge>}
                </div>
                <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
                  <TokenText body={t.body} />
                </p>
              </div>
              {useHref && (
                <Button asChild size="sm" variant="outline" className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Link to={useHref(t)}>
                    <SendIcon /> Use
                  </Link>
                </Button>
              )}
            </div>
            <div className="text-muted-foreground text-xs tabular-nums">
              {t.useCount === 0 ? 'Never used' : `Used ${t.useCount} ${t.useCount === 1 ? 'time' : 'times'}${t.lastUsedAt ? ` · last ${fmtRelative(t.lastUsedAt)}` : ''}`}
            </div>
          </div>
        )}
        searchPlaceholder="Search templates, text or variables…"
        defaultSort={{ id: 'name', dir: 'asc' }}
        exportName="templates"
        filters={categories.length > 0 ? [{ key: 'category', label: 'Category', options: categories.map((c) => ({ value: c, label: c })), predicate: (t, v) => t.category === v, counts: true, kind: categories.length > 4 ? 'select' : 'segmented' }] : []}
        selectable={canManage}
        selectDisabled={(t) => !canManageScope(t)}
        bulkActions={({ selected }) => (
          <Button size="sm" variant="outline" className="text-destructive" disabled={busy} onClick={() => setBulkDeleting(selected)}>
            <Trash2Icon /> Delete {selected.length}
          </Button>
        )}
        emptyState={
          <EmptyState
            icon={FileTextIcon}
            title="No templates yet"
            description="Save the notices you send again and again: gate changes, start times, timecard reminders. Variables handle the parts that change, and names can be filled in per person. Write one here, or save a message from Compose."
            action={
              canManage ? (
                <Button onClick={openNew} disabled={busy}>
                  <PlusIcon /> New template
                </Button>
              ) : undefined
            }
          />
        }
      />

      {automationJobId && <AutomationMessages jobId={automationJobId} canManage={canManage} onCopy={(name, body, category) => mutations.create.mutateAsync({ name, body, category }).then(() => toast.success(`Saved “${name}” as a template`)).catch((err: unknown) => toast.error(errorMessage(err)))} />}

      <VariablesGuide />

      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editing}
        scopeLabel={readOnly ? 'Shared by the organization' : scopeLabel}
        readOnly={readOnly}
        editHref={readOnly ? orgHref : undefined}
        busy={mutations.create.isPending || mutations.update.isPending}
        onSave={(body) => (editing ? mutations.update.mutateAsync({ id: editing.id, body }) : mutations.create.mutateAsync(body))}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete “${deleting?.name}”?`}
        description="Messages already sent from it are not affected."
        confirmLabel="Delete"
        destructive
        loading={mutations.remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await mutations.remove.mutateAsync(deleting.id);
            toast.success('Template deleted');
            setDeleting(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
      <ConfirmDialog
        open={!!bulkDeleting}
        onOpenChange={(o) => !o && setBulkDeleting(null)}
        title={`Delete ${bulkDeleting?.length ?? 0} templates?`}
        description="Messages already sent from them are not affected."
        confirmLabel="Delete"
        destructive
        loading={busy}
        onConfirm={async () => {
          if (!bulkDeleting) return;
          await removeMany(bulkDeleting);
          setBulkDeleting(null);
        }}
      />
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</dt>
      <dd className="mt-0.5 flex items-baseline gap-2">
        <span className="font-display text-2xl leading-none tabular-nums">{value.toLocaleString()}</span>
        {hint && <span className="text-muted-foreground text-xs">{hint}</span>}
      </dd>
    </div>
  );
}

/** The three kinds of variable, with the exact tokens to type, folded away until someone needs them. */
/** The texts a job's weather and schedule rules send, with a way to keep one as an ordinary template. */
function AutomationMessages({ jobId, canManage, onCopy }: { jobId: string; canManage: boolean; onCopy: (name: string, body: string, category: string) => Promise<unknown> }) {
  const automation = useAutomation(jobId);
  const rules = automation.data?.items ?? [];
  const [copying, setCopying] = useState<string | null>(null);
  if (!automation.data || rules.length === 0) return null;
  return (
    <section className="bg-card border" aria-label="Automation messages">
      <div className="flex flex-wrap items-baseline justify-between gap-3 px-5 pt-4 pb-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Automation messages</h3>
          <p className="text-muted-foreground mt-0.5 text-xs">What the weather and schedule rules on this job send when they fire. Edit the text on the rule; keep a copy here to start from on Compose.</p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/jobs/${jobId}/automation`}>All rules</Link>
        </Button>
      </div>
      <ul className="divide-y border-t">
        {rules.map((r) => (
          <li key={r.id} className={cn('grid gap-2 px-5 py-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start', !r.enabled && 'opacity-60')}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{r.name}</span>
                <Badge variant="muted">{r.kind === 'weather' ? 'Weather rule' : 'Reminder'}</Badge>
                <Badge variant={r.mode === 'auto' ? 'default' : 'outline'}>{r.mode === 'auto' ? 'Auto-send' : 'Draft'}</Badge>
                {!r.enabled && <Badge variant="outline">Paused</Badge>}
              </div>
              <p className="text-muted-foreground mt-0.5 text-xs">{describeTrigger(r)}</p>
              <p className="mt-1.5 text-sm leading-relaxed">
                <TokenText body={r.action.template} />
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button asChild variant="outline" size="sm">
                <Link to={`/jobs/${jobId}/automation/${r.id}`}>
                  <PencilIcon /> Edit rule
                </Link>
              </Button>
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={copying === r.id}
                  onClick={() => {
                    setCopying(r.id);
                    void onCopy(r.name, r.action.template, r.kind === 'weather' ? 'Weather' : 'Reminders').finally(() => setCopying(null));
                  }}
                >
                  <CopyIcon /> {copying === r.id ? 'Saving…' : 'Save as template'}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VariablesGuide() {
  return (
    <details className="group border">
      <summary className="text-muted-foreground hover:text-foreground flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm select-none">
        <BracesIcon className="size-4" aria-hidden />
        <span className="font-medium">How variables work</span>
        <span className="ml-auto text-xs group-open:hidden">Show</span>
        <span className="ml-auto hidden text-xs group-open:inline">Hide</span>
      </summary>
      <div className="grid gap-6 border-t p-4 md:grid-cols-3">
        <GuideColumn kind="builtin" title="Filled automatically" blurb="From the job, the clock and whoever sends it, the moment the template is used.">
          {BUILTIN_VARIABLES.map((v) => (
            <GuideRow key={v.key} kind="builtin" token={`{{${v.key}}}`} label={v.label} example={v.example} />
          ))}
        </GuideColumn>
        <GuideColumn kind="contact" title="Personalized per recipient" blurb="Filled in for each person as the text goes out. Add a fallback for contacts missing the field.">
          {CONTACT_VARIABLES.map((v) => (
            <GuideRow key={v.key} kind="contact" token={`{{${v.key}}}`} label={v.label} example={v.example} />
          ))}
          <GuideRow kind="contact" token="{{contact.firstName|there}}" label="With a fallback" example="Terrance, or “there”" />
        </GuideColumn>
        <GuideColumn kind="custom" title="Asked when used" blurb="Anything else you put in double braces becomes a blank to fill in on Compose.">
          <GuideRow kind="custom" token="{{gate}}" label="Gate" example="Gate B" />
          <GuideRow kind="custom" token="{{day}}" label="Day" example="Thursday" />
          <GuideRow kind="custom" token="{{reason}}" label="Reason" example="crane work" />
          <GuideRow kind="custom" token="{{start}}" label="Start" example="6:30 AM" />
        </GuideColumn>
      </div>
    </details>
  );
}

function GuideColumn({ kind, title, blurb, children }: { kind: 'builtin' | 'contact' | 'custom'; title: string; blurb: string; children: React.ReactNode }) {
  return (
    <div className="grid content-start gap-2">
      <div className="flex items-center gap-2">
        <span className={cn('size-2', kind === 'contact' ? 'bg-primary' : kind === 'custom' ? 'bg-foreground' : 'bg-muted-foreground/60')} aria-hidden />
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
      <p className="text-muted-foreground text-xs leading-relaxed">{blurb}</p>
      <ul className="mt-1 grid gap-1">{children}</ul>
    </div>
  );
}

function GuideRow({ kind, token, label, example }: { kind: 'builtin' | 'contact' | 'custom'; token: string; label: string; example: string }) {
  return (
    <li className="flex items-baseline justify-between gap-3 text-xs">
      <VariableChip kind={kind} title={label}>
        {token}
      </VariableChip>
      <span className="text-muted-foreground truncate">{example}</span>
    </li>
  );
}
